/**
 * Lane A JSON-RPC URL resolution (#940) — the ONE place that decides where
 * Lane A calls go. Every reader that used `import.meta.env.VITE_RPC_URL`
 * directly (RpcClient, useAuth) must come through `resolveRpcUrl()`.
 *
 * Why: `VITE_RPC_URL` is inlined at BUILD time, so a released image talks to
 * the Lane A it was built for wherever it is deployed (a staging console on a
 * prod-built image would call prod Lane A). The production container therefore
 * serves `/runtime-config.json` → `{"rpcUrl": "..."}`, written at container
 * START from the runtime env var `NUDATA_RPC_URL`
 * (infra/frontend/write-runtime-config.cjs). Resolution order:
 *
 *   1. runtime `rpcUrl` — if the file exists and the value is usable;
 *   2. build-time `VITE_RPC_URL`;
 *   3. `http://localhost:8080/rpc` (local dev default).
 *
 * The config is fetched once (concurrent callers share the request) and a
 * definitive answer is cached for the page's lifetime.
 *
 * FAIL CLOSED on transient failure (network error, 3 s timeout/abort, 5xx):
 * the fetch is retried up to RUNTIME_CONFIG_MAX_ATTEMPTS times with a short
 * backoff inside one resolution; if every attempt fails, `resolveRpcUrl()`
 * REJECTS with `RuntimeConfigUnavailableError` so no RPC is sent. It must
 * never fall back to the build-time URL here — released images bake the PROD
 * Lane A URL, so a staging console would send a staging user's credentials to
 * PROD Lane A (the exact thing #940 prevents). The failure is not cached; the
 * next call retries.
 *
 * Definitive "no runtime value" answers DO fall back to the build-time URL
 * (and are cached): `{}` / missing `rpcUrl` (the documented "unset" answer —
 * the container entrypoint always writes the file), plus 404, a non-JSON
 * (HTML SPA) body, a non-object body, or an invalid `rpcUrl`. All but the
 * `{}` case console.warn loudly, since in a PROD bundle they mean the
 * container did not serve the file it is supposed to.
 *
 * Only production bundles fetch it — `vite dev` and Jest never have a
 * runtime-config.json to read.
 */

/* global AbortController -- browser global missing from the src/ eslint env */

export const RUNTIME_CONFIG_PATH = "/runtime-config.json";
const DEV_DEFAULT_RPC_URL = "http://localhost:8080/rpc";
const FETCH_TIMEOUT_MS = 3000;
const MAX_URL_LENGTH = 2048;
/** Attempts per resolution before failing closed (#940). */
export const RUNTIME_CONFIG_MAX_ATTEMPTS = 3;
const RETRY_BACKOFF_MS = [200, 400];

/**
 * Thrown by `resolveRpcUrl()` in a production bundle when the runtime config
 * could not be read (network error, timeout, 5xx) after every retry. No Lane A
 * request may be sent when this is raised.
 */
export class RuntimeConfigUnavailableError extends Error {
  readonly cause: unknown;
  constructor(cause?: unknown) {
    super(
      `Lane A endpoint unavailable: could not load ${RUNTIME_CONFIG_PATH}. Please retry.`,
    );
    this.name = "RuntimeConfigUnavailableError";
    this.cause = cause;
  }
}

/** The URL baked into this bundle at build time. */
export function buildTimeRpcUrl(): string {
  return import.meta.env.VITE_RPC_URL || DEV_DEFAULT_RPC_URL;
}

/** Absolute http(s) URL, no credentials, path ending in `/rpc`. */
export function isUsableRpcUrl(value: unknown): value is string {
  if (typeof value !== "string" || value.length > MAX_URL_LENGTH) return false;
  let url: URL;
  try {
    url = new URL(value); // throws on relative input — no base on purpose
  } catch {
    return false;
  }
  if (url.protocol !== "https:" && url.protocol !== "http:") return false;
  if (url.username || url.password) return false;
  return url.pathname.endsWith("/rpc");
}

function runtimeConfigEnabled(): boolean {
  // Vite sets PROD (boolean) in `vite build` output only.
  const prod = (import.meta.env as Record<string, unknown>).PROD;
  return prod === true || prod === "true";
}

/** Marks a failure worth retrying (network, timeout, 5xx). */
class TransientConfigError extends Error {
  readonly cause: unknown;
  constructor(message: string, cause?: unknown) {
    super(message);
    this.name = "TransientConfigError";
    this.cause = cause;
  }
}

function warnFallback(reason: string): string {
  console.warn(
    `[rpcUrl] ${reason} from ${RUNTIME_CONFIG_PATH} in a production bundle; ` +
      "using the build-time VITE_RPC_URL (#940)",
  );
  return buildTimeRpcUrl();
}

/** One attempt. Resolves a definitive URL or throws TransientConfigError. */
async function fetchRuntimeRpcUrlOnce(): Promise<string> {
  const controller =
    typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS)
    : null;
  try {
    let res: Awaited<ReturnType<typeof fetch>>;
    try {
      res = await fetch(RUNTIME_CONFIG_PATH, {
        cache: "no-store",
        credentials: "same-origin",
        headers: { Accept: "application/json" },
        signal: controller?.signal,
      });
    } catch (err) {
      throw new TransientConfigError("network error or timeout", err);
    }
    // 408/429 are "try again" answers, like 5xx — never a reason to fall
    // back to (and cache) the build-time URL.
    if (res.status >= 500 || res.status === 408 || res.status === 429) {
      throw new TransientConfigError(`HTTP ${res.status}`);
    }
    if (!res.ok) return warnFallback(`HTTP ${res.status}`);

    let body: unknown;
    try {
      body = await res.json();
    } catch (err) {
      if (controller?.signal.aborted || !(err instanceof SyntaxError)) {
        // A dropped connection mid-body (TypeError) is transient, not a
        // definitive "no config" answer — never fall back and cache on it.
        throw new TransientConfigError("failed reading body", err);
      }
      // A parse failure: `serve -s` answers a missing file with index.html (200).
      return warnFallback("non-JSON (HTML?) response");
    }
    if (body === null || typeof body !== "object" || Array.isArray(body)) {
      return warnFallback("non-object JSON body");
    }
    const rpcUrl = (body as { rpcUrl?: unknown }).rpcUrl;
    if (rpcUrl === undefined || rpcUrl === null) {
      // `{}` — the documented "NUDATA_RPC_URL unset" answer. Quiet fallback.
      return buildTimeRpcUrl();
    }
    if (!isUsableRpcUrl(rpcUrl)) return warnFallback("invalid rpcUrl");
    return rpcUrl;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Bounded retry; rejects with RuntimeConfigUnavailableError (fail closed). */
async function loadRuntimeRpcUrl(): Promise<string> {
  let lastError: unknown;
  for (let attempt = 1; attempt <= RUNTIME_CONFIG_MAX_ATTEMPTS; attempt++) {
    try {
      return await fetchRuntimeRpcUrlOnce();
    } catch (err) {
      lastError = err;
      if (attempt < RUNTIME_CONFIG_MAX_ATTEMPTS) {
        await sleep(RETRY_BACKOFF_MS[attempt - 1] ?? 400);
      }
    }
  }
  console.error(
    `[rpcUrl] could not load ${RUNTIME_CONFIG_PATH} after ${RUNTIME_CONFIG_MAX_ATTEMPTS} attempts; ` +
      "refusing to fall back to the build-time Lane A URL (#940)",
    lastError,
  );
  throw new RuntimeConfigUnavailableError(lastError);
}

let cached: string | null = null;
let inflight: Promise<string> | null = null;

/**
 * Resolve the Lane A RPC URL (see module doc). Safe to call per request.
 * In a production bundle this REJECTS with RuntimeConfigUnavailableError when
 * the runtime config is transiently unreachable — callers must treat that as a
 * failed request and send nothing.
 */
export function resolveRpcUrl(): Promise<string> {
  if (cached !== null) return Promise.resolve(cached);
  if (!runtimeConfigEnabled()) {
    cached = buildTimeRpcUrl();
    return Promise.resolve(cached);
  }
  if (!inflight) {
    inflight = loadRuntimeRpcUrl().then(
      (url) => {
        cached = url;
        inflight = null;
        return url;
      },
      (err: unknown) => {
        inflight = null; // never cache a failure — the next call retries
        throw err;
      },
    );
  }
  return inflight;
}
